import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type TypeformInfo,
} from '../types';

const mapTypeformInfo = (data: unknown): TypeformInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    user_info?: {
      name: string;
      email: string;
    };
    state?: IMPORT_STATE;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
  };

  if (!info.alias) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    user_info: info.user_info,
    import_state: info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
  };
};

export const useTypeform = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(
    SOURCE_TYPE.TYPEFORM,
    workspaceAlias,
  );

  const { data: typeformWorkspace } = useQuery({
    queryKey,
    queryFn: async () =>
      mapTypeformInfo(await service.typeform.getTypeformInfo(workspaceAlias)),
    enabled,
  });

  const typeformAlias = typeformWorkspace?.alias ?? '';

  const { mutate: disconnectTypeformMutation } = useMutation({
    mutationFn: () => service.typeform.deleteTypeformInfo(typeformAlias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    typeformWorkspace,
    disconnectTypeformMutation,
    invalidateTypeformQuery: () => queryClient.invalidateQueries({ queryKey }),
  };
};
